import { Stack } from '@mui/material';
import { FieldValues } from 'react-hook-form';

import {
  FormAwareElementProps,
  FormAwareInputProps,
} from '@/components/form/formAwareInputs/FormAwareInput.types';
import { AISuggestedValue } from '@/modules/aiSuggestions/AISuggestedValue/AISuggestedValue';
import { SuggestableKind } from '@/modules/aiSuggestions/aiSuggestions.types';
import {
  FormAwareLocateSuggestionButton,
  FormAwareLocateSuggestionButtonVariant,
} from '@/modules/aiSuggestions/components/forms/FormAwareLocateSuggestionButton';
import { useTestamentaryEntitySuggestionsContextIfAvailable } from '@/modules/aiSuggestions/context/TestamentaryEntitySuggestions.context';
import { useSuggestableInputHandlers } from '@/modules/aiSuggestions/hooks/useSuggestableInputHandlers';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';

interface SuggestableFormAwareInputTestamentaryEntityProps<
  S extends FieldValues,
  T extends FormAwareInputProps<S>,
> {
  suggestionKind: SuggestableKind;
  FormAwareElement: FormAwareElementProps<S, T>;
}

type SuggestableFormAwareInputTestamentaryEntityCombinedProps<
  S extends FieldValues,
  T extends FormAwareInputProps<S>,
> = SuggestableFormAwareInputTestamentaryEntityProps<S, T> & T;

/**
 * A wrapper around a FormAwareInput that displays a suggested value for the
 * field, and allows the user to accept or reject the suggestion.
 *
 * This is the testamentary entity version that uses TestamentaryEntitySuggestionsContext
 * instead of EntitySuggestionsContext.
 *
 * If accepted, the suggestion will be set as the field's value, and the form
 * will be marked as dirty.
 *
 * NOTE: This only supports suggestions for fields that are single-valued, like
 * text inputs, single select inputs, date pickers, etc.
 */
export function SuggestableFormAwareInputTestamentaryEntity<
  S extends FieldValues,
  T extends FormAwareInputProps<S>,
>(props: SuggestableFormAwareInputTestamentaryEntityCombinedProps<S, T>) {
  const subtrustAISuggestionsEnabled = useFeatureFlag(
    'aikg_v2_associations_poc'
  );

  const { fieldName, suggestionKind, FormAwareElement } = props;

  // Use the "if available" variant since this component may be rendered
  // before the provider is mounted or when the flag is off
  const suggestionsContext =
    useTestamentaryEntitySuggestionsContextIfAvailable();

  const {
    suggestion,
    isAcknowledged,
    handleAcceptSuggestion,
    handleRejectSuggestion,
  } = useSuggestableInputHandlers({
    fieldName,
    suggestionKind,
    getSuggestableSuggestion: suggestionsContext?.getSuggestableSuggestion,
  });

  if (!subtrustAISuggestionsEnabled) {
    // Return the FormAwareElement as is, if AI suggestions are disabled
    return <FormAwareElement {...props} />;
  }

  return (
    <Stack>
      <FormAwareElement
        {...props}
        labelIconEnd={
          <FormAwareLocateSuggestionButton
            suggestion={suggestion}
            variant={FormAwareLocateSuggestionButtonVariant.MATCHER}
          />
        }
      />
      {suggestion && !isAcknowledged && (
        <AISuggestedValue
          suggestion={suggestion}
          onAcceptSuggestion={handleAcceptSuggestion}
          onRejectSuggestion={handleRejectSuggestion}
        />
      )}
    </Stack>
  );
}
